import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import merge from 'lodash.merge';
import { DateTime } from 'luxon';

import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Dialog from '@material-ui/core/Dialog';
import Grid from '@material-ui/core/Grid';
import Collapse from '@material-ui/core/Collapse';
import Link from '@material-ui/core/Link';
import DropDownIcon from '@material-ui/icons/ArrowDropDownCircle';

import { getYearsAndMonthsFromLuxon, sqlDateToYearAndMonth, yearAndMonthToSql } from 'helpers/helpers';
import compose from 'helpers/compose';
import withoutEnterSubmit from 'components/withoutEnterSubmit';
import ControlButtons from 'components/ControlButtons';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import FormTextField from 'components/FormFields/FormTextField';
import FormNumberField from 'components/FormFields/FormNumberField';
import FormSelectField from 'components/FormFields/FormSelectField';
import InflationRateField from 'components/FormFields/InflationRateField';
import FormDateSelects from 'components/FormFields/FormDateSelects';
import GridSubtitle from 'components/GridSubtitle/GridSubtitle';
import Switch from 'components/FormFields/Switch';
import Info from 'components/Tooltip/Info';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import useStyles from './styles/moduleStyles';

import { WorkflowPlanStore } from 'stores/workflowStores/newWorkflowPlanStore/workflowPlanStore';
import { TargetsStore } from 'stores/workflowStores/finances/targets/targetsStore';
import {
  getInitialValues,
  Target,
  TargetOptions,
  TargetFrequency,
  TargetType,
} from 'stores/workflowStores/finances/targets/model';
import { Currency, AccountMode } from 'enums';

const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

interface OwnProps {
  opened: boolean;
  handleClose: () => void;
  currency: Currency;
  targetId?: string;
  refresh: () => void;
  options: TargetOptions;
  defaultInflation: number;
  defaultInflationType: string;
  mode: AccountMode;
}

interface Stores {
  targetsStore: TargetsStore;
  planStore?: WorkflowPlanStore;
}

type Props = OwnProps & Stores;

const minTargetValue = (currency: Currency) => {
  switch (currency) {
    case 'EUR':
      return 20;
    case 'CZK':
    default:
      return 500;
  }
};

const AdditionalTargetModal: React.FC<Props> = ({
  targetId,
  opened,
  handleClose,
  targetsStore,
  planStore,
  currency,
  refresh,
  options,
  defaultInflation,
  defaultInflationType,
  mode,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  let formSubmitting = false;
  const formKey = 'AdditionalTargetForm';
  const { fetchTarget, handleSubmit, data } = targetsStore;
  const [lowValueModalOpen, setLowValueModalOpen] = useState(false);
  const [showDetail, setShowDetail] = useState(!targetId);

  useEffect(() => {}, [targetId, fetchTarget, opened]);

  useEffect(() => {
    (async () => {
      if (opened && targetId) {
        await fetchTarget(targetId);
      }
    })();
  }, [targetId, fetchTarget, opened]);

  useEffect(() => {
    if (data?.inflation.enabled) setShowDetail(true);
  }, [data?.inflation.enabled]);

  const onSubmit = async (values: Target, formikHelpers: FormikHelpers<Target>) => {
    const ok = await handleSubmit(values, formikHelpers);
    if (ok) {
      handleClose();
      refresh();
    }
  };

  const minDate = useMemo(
    () =>
      options?.minDate
        ? sqlDateToYearAndMonth(options?.minDate)
        : getYearsAndMonthsFromLuxon(DateTime.local().plus({ months: 1 })),
    [options?.minDate],
  );
  const maxDate = useMemo(() => sqlDateToYearAndMonth(options?.maxDate), [options]);

  // change until month based on since value
  const changeUntilMonth = (formikProps: FormikProps<Target>, untilYearsValues?: number[]) => {
    const {
      values: { frequency, since, until },
    } = formikProps;
    let monthToDisabled = false;

    if (frequency === TargetFrequency.REPEAT_EVERY || frequency === TargetFrequency.YEARLY) {
      monthToDisabled = true;

      if (since) {
        const { years: sinceYear, months: sinceMonth } = sqlDateToYearAndMonth(since);
        const dateUntil = sqlDateToYearAndMonth(until);

        if (sinceMonth !== dateUntil.months) {
          let newSinceYear = dateUntil.years || (untilYearsValues && untilYearsValues[0]) || sinceYear + 1;
          const newUntil = yearAndMonthToSql(newSinceYear, sinceMonth);

          formikProps.setFieldValue('until', newUntil, false);
        }
      }
    }
    return monthToDisabled;
  };

  const getUntilYearsValues = (formikProps: FormikProps<Target>): number[] | undefined => {
    const {
      values: { frequency, since, repeatYears },
    } = formikProps;
    const { maxDate } = options;

    if (frequency === TargetFrequency.REPEAT_EVERY && since && repeatYears) {
      const repeatValue = parseInt(repeatYears);
      const { years: sinceYear } = sqlDateToYearAndMonth(since);
      const { years: maxYear } = sqlDateToYearAndMonth(maxDate);

      const yearsValues = [];
      for (let year = sinceYear + repeatValue; year <= repeatValue * 20 + sinceYear; year = year + repeatValue) {
        if (year > maxYear) break;
        yearsValues.push(year);
      }
      return yearsValues;
    }
    return undefined;
  };

  const controlUntilYearValue = async (formikProps: FormikProps<Target>, untilYearsValues: number[] | undefined) => {
    const {
      values: { until, since },
    } = formikProps;

    if (until) {
      const { years: untilYear, months: untilMonth } = sqlDateToYearAndMonth(until);

      // control if new year value is in untilYearsValues
      if (untilYearsValues && !untilYearsValues.includes(untilYear)) {
        const newUntil = yearAndMonthToSql(untilYearsValues[0], untilMonth);
        await formikProps.setFieldValue('until', newUntil);
        return;
      }

      const { years: sinceYear, months: sinceMonth } = sqlDateToYearAndMonth(since);

      // control if year until is not smaller then since year
      if (sinceYear > untilYear) {
        const newUntil = yearAndMonthToSql((untilYearsValues && untilYearsValues[0]) || sinceYear + 1, untilMonth);
        await formikProps.setFieldValue('until', newUntil);
        return;
      }
      if (sinceYear === untilYear && sinceMonth > untilMonth) {
        const newUntil = yearAndMonthToSql(sinceYear, sinceMonth + 1);
        await formikProps.setFieldValue('until', newUntil);
        return;
      }
    }
  };

  const handleCloneTarget = async () => {
    const id = await targetsStore.cloneActiveTarget(targetId!);
    if (planStore && id) {
      handleClose();
      await refresh();
      planStore.setEditTarget(id);
    }
  };

  const initialValues = {
    ...getInitialValues(currency),
    inflation: { enabled: true, valuation: true, rate: defaultInflation, type: defaultInflationType },
  };

  return data?.active ? (
    <ConfirmDialog
      isOpen={opened}
      close={handleClose}
      confirm={handleCloneTarget}
      title={null}
      btnCloseText={'general_cancel'}
      btnConfirmText={'general_modify'}
    >
      {t('additionalTargets_active_cantEdit')}
    </ConfirmDialog>
  ) : (
    <>
      <Dialog maxWidth="md" open={opened}>
        <DialogHeader onClose={handleClose}>{t('additionalTarget_set')}</DialogHeader>
        <DialogContent>
          <Formik
            initialValues={data && targetId ? merge({}, initialValues, data) : initialValues}
            onSubmit={onSubmit}
            enableReinitialize
          >
            {(formikProps) => {
              formSubmitting = formikProps.isSubmitting;

              const untilYearsValues = getUntilYearsValues(formikProps);
              const monthUntilDisabled = changeUntilMonth(formikProps, untilYearsValues);

              //controlUntilYearValue(formikProps, untilYearsValues);

              return (
                <FormWithoutEnterSubmit id={t(formKey)}>
                  <Grid container>
                    {formikProps.values.type !== TargetType.PORTFOLIO_RENT && (
                      <Grid item xs={12}>
                        <Grid item xs={6}>
                          <Switch
                            name="enabled"
                            label={t('additionalTargets_switch_active')}
                            tooltipContent={
                              formikProps.values.enabled
                                ? t('additionalTargets_switch_plan_included')
                                : t('additionalTargets_switch_plan_excluded')
                            }
                          />
                        </Grid>
                      </Grid>
                    )}
                    <Grid item xs={6}>
                      {formikProps.values.type !== '' && !options.type.includes(formikProps.values.type) ? (
                        <FormSelectField
                          name="type"
                          label={t('additionalTargets_targetType')}
                          options={[
                            {
                              value: formikProps.values.type,
                              label: t(`additionalTargets_type_${formikProps.values.type}`),
                            },
                          ]}
                          tooltipContent={t(
                            `tooltip_additionalTargets_targetType${mode === AccountMode.LTIP ? '_LTIP' : ''}`,
                          )}
                        />
                      ) : (
                        <FormSelectField
                          name="type"
                          label={t('additionalTargets_targetType')}
                          options={options.type.map((type) => ({
                            value: type,
                            label: t(`additionalTargets_type_${type}`),
                          }))}
                          tooltipContent={t(
                            `tooltip_additionalTargets_targetType${mode === AccountMode.LTIP ? '_LTIP' : ''}`,
                          )}
                        />
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <FormTextField
                        name="title"
                        label={t('additionalTargets_title')}
                        tooltipContent={t('tooltip_additionalTargets_title')}
                        maxLength={50}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormSelectField
                        name="frequency"
                        label={t('general_frequency')}
                        options={options.frequency.map((freq) => ({
                          value: freq,
                          label: t(`general_frequency_${freq}`),
                        }))}
                        fast={false}
                      />
                    </Grid>
                    <Grid container item xs={6} alignItems="center">
                      {formikProps.values.frequency === TargetFrequency.REPEAT_EVERY && (
                        <>
                          <Grid item xs={6}>
                            <FormSelectField
                              name="repeatYears"
                              label={t('general_Count')}
                              options={new Array(options.maxYears - options.minYears + 1).fill(0).map((year, i) => ({
                                value: `${i + options.minYears}`,
                                label: `${i + options.minYears}`,
                              }))}
                              tooltipContent={t('tooltip_additionalTargets_repeating')}
                              afterChange={() => {
                                setTimeout(() => {
                                  const value = untilYearsValues && untilYearsValues[0];
                                  const {
                                    values: { until },
                                  } = formikProps;
                                  const { months: sinceMonth } = sqlDateToYearAndMonth(until);

                                  if (value) {
                                    const newUntil = yearAndMonthToSql(value, sinceMonth);
                                    formikProps.setFieldValue('until', newUntil);
                                  }
                                }, 0);
                              }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            {t('general_repeatYears')}
                          </Grid>
                        </>
                      )}
                    </Grid>

                    <Grid item xs={6}>
                      <FormNumberField
                        name="value.value"
                        label={t('additionalTargets_value')}
                        endAdornment={t(`currency_${currency}`)}
                        tooltipContent={t('tooltip_additionalTargets_value')}
                        afterBlur={() =>
                          formikProps.values['value'].value < minTargetValue(currency) && setLowValueModalOpen(true)
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormTextField
                        name="pay"
                        label={t('additionalTargets_pay')}
                        disabled
                        value={
                          formikProps.values.enabled === false
                            ? t('additionalTargets_pay_DISABLED')
                            : formikProps.values.frequency === TargetFrequency.SINGLE
                            ? t('additionalTargets_pay_SINGLE')
                            : t('additionalTargets_pay_OTHER')
                        }
                        tooltipContent={t('tooltip_additionalTargets_pay')}
                        fast={false}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <GridSubtitle>
                        {t(`general_${formikProps.values.frequency === TargetFrequency.SINGLE ? 'When' : 'Since'}`)}
                        <Info
                          content={t(
                            mode === AccountMode.LTIP
                              ? 'finQuestionnary_rent_from_LTIP'
                              : data?.active
                              ? 'additionalTargets_active_SinceLocked'
                              : `tooltip_additionalTargets_${
                                  formikProps.values.frequency === TargetFrequency.SINGLE ? 'when' : 'since'
                                }`,
                          )}
                          heading
                        />
                      </GridSubtitle>
                      <FormDateSelects
                        name={`since`}
                        minDate={minDate}
                        maxDate={maxDate}
                        disabled={data?.active}
                        afterChange={async () => await controlUntilYearValue(formikProps, untilYearsValues)}
                      />
                    </Grid>
                    {formikProps.values.frequency !== TargetFrequency.SINGLE &&
                      formikProps.values.frequency !== TargetFrequency.EMPTY &&
                      formikProps.values.type !== TargetType.PORTFOLIO_RENT && (
                        <Grid item xs={6}>
                          <GridSubtitle>
                            {t(`general_Until`)} <Info content={t('tooltip_additionalTargets_until')} heading />
                          </GridSubtitle>
                          <FormDateSelects
                            name={`until`}
                            minDate={
                              formikProps.values.since ? sqlDateToYearAndMonth(formikProps.values.since) : minDate
                            }
                            maxDate={maxDate}
                            readOnlyMonth={monthUntilDisabled}
                            yearsValues={untilYearsValues}
                            fast={false}
                            afterChange={async () => await controlUntilYearValue(formikProps, untilYearsValues)}
                          />
                        </Grid>
                      )}
                  </Grid>

                  <Link
                    className={classes.detailLink}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      setShowDetail(!showDetail);
                    }}
                  >
                    <span className={classes.detailControl}>
                      {t('general_advanced')}
                      <DropDownIcon className={`${showDetail ? classes.shownIcon : ''}`} />
                    </span>
                  </Link>

                  <Collapse in={showDetail}>
                    <Grid container>
                      <Grid item xs={12} />
                      <Grid item xs={6}>
                        <Switch
                          name={`inflation.enabled`}
                          label={t('general_useInflation')}
                          tooltipContent={t('additionalTargets_inflation_tooltip')}
                          afterChange={() => {
                            if (!formikProps.values.inflation.enabled) {
                              formikProps.setFieldValue('inflation.valuation', false);
                              formikProps.setFieldValue('inflation.type', '');
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <InflationRateField
                          name="inflation.rate"
                          label={t('general_annualInflation')}
                          {...(!formikProps.values.inflation.enabled ? { disabled: true } : {})}
                        />
                      </Grid>

                      <Grid item xs={12} />
                      <Grid item xs={6}>
                        <Switch
                          name={`inflation.valuation`}
                          label={t('additionalTargets_useInflation')}
                          tooltipContent={t('additionalTargets_useInflation_tooltip')}
                          disabled={!formikProps.values.inflation.enabled}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <FormSelectField
                          name="inflation.type"
                          options={options.inflationType.map((type) => ({
                            value: type,
                            label: t(`general_inflationType_${type}`),
                          }))}
                          fast={false}
                          disabled={!formikProps.values.inflation?.enabled || !formikProps.values.inflation?.valuation}
                          tooltipContent={t('additionalTargets_useInflationType_tooltip')}
                        />
                      </Grid>
                    </Grid>
                  </Collapse>
                </FormWithoutEnterSubmit>
              );
            }}
          </Formik>
        </DialogContent>
        <DialogActions>
          <ControlButtons
            isSubmitting={formSubmitting}
            nextText={t('general_save')}
            form={formKey}
            nextButtonId="additionalTarget-modal-submit"
            backText={t('general_cancel')}
            onBack={handleClose}
            onClick={() => {}}
          />
        </DialogActions>
      </Dialog>

      <ConfirmDialog isOpen={lowValueModalOpen} close={() => setLowValueModalOpen(false)} title={null}>
        <span
          dangerouslySetInnerHTML={{
            __html: t(`additionalTargets_lowValueInfo`, {
              amount: `${minTargetValue(currency)} ${t('currency_' + currency)}`,
              interpolation: { escapeValue: false },
            }),
          }}
        />
      </ConfirmDialog>
    </>
  );
};

export default compose(observer)(AdditionalTargetModal);
